/**
 * PropertyCWV — Core Web Vitals lens for one property.
 *
 * Reads from CrUX (real-user p75 field data) + PSI (lab Lighthouse).
 * Both stored in source_data; relevant slices folded into the
 * cwv_snapshots + cwv_lighthouse substrate (migration 0013).
 *
 * The 7-tab structure mirrors the Ahrefs/GSC/GA4 lenses:
 *   Origin · Per-URL · Trends · Lighthouse · Failing × Traffic ·
 *   Mobile vs Desktop · Recommendations
 */
import { useParams } from "react-router-dom";
import { Card } from "@tremor/react";
import {
  Gauge, Monitor, Smartphone, AlertTriangle, ListChecks, Lightbulb,
  TrendingDown,
} from "lucide-react";
import {
  useProperty, useCapabilitySample, useStartCWVPull,
} from "@/lib/queries";
import { VendorLens } from "@/components/VendorLens";
import { cn } from "@/lib/utils";


// ─── Types & helpers ───────────────────────────────────────────────

type Verdict = "good" | "needs_improvement" | "poor" | null | undefined;

interface CruxMetric {
  p75?: number | string | null;
  verdict?: string;
}

interface CruxResponse {
  metrics?: Record<string, CruxMetric>;
  crux_no_data?: boolean;
}

interface LighthouseAuditDetailsItem {
  url?: string;
  source?: string | { url?: string };
  totalBytes?: number;
  wastedBytes?: number;
  wastedMs?: number;
  wastedPercent?: number;
  duration?: number;
  // Plus arbitrary other keys depending on audit type
  [k: string]: unknown;
}

interface LighthouseAuditDetails {
  type?: string;       // 'opportunity' | 'table' | 'list' | etc
  headings?: Array<{ key?: string; label?: string; valueType?: string }>;
  items?: LighthouseAuditDetailsItem[];
  overall_savings_ms?: number;
  overall_savings_bytes?: number;
}

interface LighthouseAudit {
  id?: string;
  title?: string;
  description?: string;
  displayValue?: string;
  score?: number;
  numericValue?: number;
  numericUnit?: string;
  scoreDisplayMode?: string;
  warnings?: string[];
  details?: LighthouseAuditDetails;
}

interface PSIResponse {
  url?: string;
  strategy?: string;
  field_url?: { metrics?: Record<string, CruxMetric>; no_data?: boolean };
  field_origin?: { metrics?: Record<string, CruxMetric>; no_data?: boolean };
  lab?: {
    final_url?: string;
    fetch_time?: string;
    scores?: Record<string, number | null>;
    core_web_vitals?: Record<string, { displayValue?: string; numericValue?: number; score?: number }>;
    top_opportunities?: LighthouseAudit[];
    seo_failures?: LighthouseAudit[];
    a11y_failures?: LighthouseAudit[];
    runtime_error?: string;
    no_data?: boolean;
  };
}

const METRIC_LABEL: Record<string, string> = {
  largest_contentful_paint: "LCP",
  interaction_to_next_paint: "INP",
  cumulative_layout_shift: "CLS",
  first_contentful_paint: "FCP",
  experimental_time_to_first_byte: "TTFB",
};

const METRIC_UNIT: Record<string, string> = {
  largest_contentful_paint: "ms",
  interaction_to_next_paint: "ms",
  cumulative_layout_shift: "",
  first_contentful_paint: "ms",
  experimental_time_to_first_byte: "ms",
};

const METRIC_GOOD_THRESHOLD: Record<string, number> = {
  largest_contentful_paint: 2500,
  interaction_to_next_paint: 200,
  cumulative_layout_shift: 0.1,
  first_contentful_paint: 1800,
  experimental_time_to_first_byte: 800,
};


function _verdictColor(verdict: Verdict): string {
  if (verdict === "good") return "text-emerald-700 dark:text-emerald-400";
  if (verdict === "needs_improvement") return "text-amber-700 dark:text-amber-400";
  if (verdict === "poor") return "text-red-700 dark:text-red-400";
  return "text-muted-foreground";
}

function _verdictBg(verdict: Verdict): string {
  if (verdict === "good") return "bg-emerald-500/10";
  if (verdict === "needs_improvement") return "bg-amber-500/10";
  if (verdict === "poor") return "bg-red-500/10";
  return "bg-muted";
}

function _formatMetric(name: string, p75: number | string | null | undefined): string {
  if (p75 === null || p75 === undefined) return "—";
  const v = typeof p75 === "string" ? Number(p75) : p75;
  if (isNaN(v)) return "—";
  const unit = METRIC_UNIT[name] || "";
  if (name === "cumulative_layout_shift") return v.toFixed(3);
  return `${v.toFixed(0)}${unit}`;
}


// ─── Page entry ────────────────────────────────────────────────────

export default function PropertyCWV() {
  const { propertyId: rawId } = useParams<{ propertyId: string }>();
  const propertyId = Number(rawId);
  const property = useProperty(propertyId);
  const pull = useStartCWVPull(propertyId);

  if (property.isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!property.data) return <div className="text-sm text-red-600">Property not found.</div>;
  const p = property.data;

  return (
    <VendorLens
      vendorIcon={<Gauge className="h-5 w-5 text-primary" />}
      vendorTitle="Core Web Vitals"
      vendorDescription={
        <>
          Real-user CWV field data from Chrome (CrUX) + lab data from
          PageSpeed Insights for <span className="font-mono">{p.domain}</span>.
          Google ranks on field, not lab — but lab gives concrete fix
          recommendations.
        </>
      }
      pullCostNote="Free (CrUX + PSI APIs). Takes ~30-60s for origin + homepage + top-25 pages."
      pullMutation={pull}
      tabs={[
        { key: "origin",   label: "Origin",   body: <OriginTab propertyId={propertyId} /> },
        { key: "perurl",   label: "Per-URL",  body: <PerURLTab propertyId={propertyId} /> },
        { key: "trends",   label: "Trends",   body: <TrendsTab propertyId={propertyId} /> },
        { key: "lighthouse", label: "Lighthouse", body: <LighthouseTab propertyId={propertyId} /> },
        { key: "failing",  label: "Failing × Traffic", body: <FailingTrafficTab propertyId={propertyId} /> },
        { key: "compare",  label: "Mobile vs Desktop", body: <CompareFormFactorTab propertyId={propertyId} /> },
        { key: "recs",     label: "Recommendations", body: <RecommendationsTab propertyId={propertyId} /> },
      ]}
    />
  );
}


// ─── Origin tab ────────────────────────────────────────────────────


function OriginTab({ propertyId }: { propertyId: number }) {
  // Note: capabilities/sample returns the most-recent row per
  // (source, endpoint, target). For the CrUX origin endpoint, both
  // PHONE and DESKTOP rows share the same target. We read field-origin
  // data from the PSI rows instead — those are partitioned by strategy
  // (mobile/desktop) so each is its own row.
  const psi = useCapabilitySample("crux", "psi", { propertyId });
  const psiBody = (psi.data?.response as PSIResponse | undefined);

  // PSI returns the field_origin AND field_url for whatever strategy
  // was last fetched. For now, surface that one (form-factor inferred
  // from `strategy`). Full mobile-vs-desktop split is in the dedicated
  // CompareFormFactor tab.
  const fieldOrigin = psiBody?.field_origin;
  const fieldFormFactor = psiBody?.strategy === "mobile" ? "Mobile" : "Desktop";

  return (
    <div className="space-y-4">
      <FormFactorCard
        title={`${fieldFormFactor} — origin field data`}
        subtitle="Real-user p75 from latest PSI run for this strategy"
        icon={fieldFormFactor === "Mobile"
          ? <Smartphone className="h-4 w-4 text-primary" />
          : <Monitor className="h-4 w-4 text-primary" />}
        metrics={fieldOrigin?.metrics}
        noData={!fieldOrigin || !!fieldOrigin.no_data}
      />
      <p className="text-[11px] text-muted-foreground">
        For full mobile-vs-desktop side-by-side, see the
        <strong> Mobile vs Desktop</strong> tab. After Refresh runs both
        strategies, the comparison table populates.
      </p>

      {psi.data?.fetched_at && (
        <div className="text-[11px] text-muted-foreground">
          Last PSI pull: {psi.data.fetched_at}
        </div>
      )}
    </div>
  );
}


function FormFactorCard({ title, subtitle, icon, metrics, noData }: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  metrics: Record<string, CruxMetric> | undefined;
  noData?: boolean;
}) {
  return (
    <Card>
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="text-[11px] text-muted-foreground">— {subtitle}</span>
      </div>
      {noData ? (
        <div className="mt-2 rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
          No CrUX field data for this form-factor (typically means too
          little real-user traffic to publish).
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
          {(["largest_contentful_paint", "interaction_to_next_paint",
             "cumulative_layout_shift", "first_contentful_paint",
             "experimental_time_to_first_byte"] as const).map((name) => {
            const m = metrics?.[name];
            const verdict = m?.verdict as Verdict;
            const goodMax = METRIC_GOOD_THRESHOLD[name];
            const goodMaxDisp = name === "cumulative_layout_shift"
              ? `${goodMax}` : `${goodMax}ms`;
            return (
              <div key={name}
                className={cn("rounded-md p-3", _verdictBg(verdict))}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {METRIC_LABEL[name]}
                </div>
                <div className={cn("mt-0.5 text-xl font-semibold", _verdictColor(verdict))}>
                  {_formatMetric(name, m?.p75)}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  good ≤ {goodMaxDisp}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}


// ─── Per-URL tab ──────────────────────────────────────────────────


function PerURLTab({ propertyId }: { propertyId: number }) {
  // The per-URL CrUX rows are stored as separate source_data rows per
  // target. For the lens we read from the cwv_snapshots substrate via
  // the inspector. Fallback: aggregate the latest CrUX url rows by
  // walking source_data via /capabilities/inspect.
  // For now, surface what we have via the most-recent-row-per-target
  // pattern. The substrate-backed table comes when we add a dedicated
  // /properties/{id}/cwv/pages endpoint (kept for v2).
  const sample = useCapabilitySample("crux", "url", { propertyId });
  const body = (sample.data?.response as CruxResponse | undefined);
  const noData = !body || body.crux_no_data;
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Per-URL field data</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Real-user CrUX p75 for the most-recently-pulled URL. To see
            the full top-N table, query the cwv_snapshots substrate via
            the Inspector for now.
          </p>
        </div>
      </div>
      {noData ? (
        <div className="mt-3 rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
          No CrUX URL-level data yet. Click <strong>Refresh</strong> at the
          top to pull origin + homepage + top GSC pages.
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
          {(["largest_contentful_paint", "interaction_to_next_paint",
             "cumulative_layout_shift", "first_contentful_paint",
             "experimental_time_to_first_byte"] as const).map((name) => {
            const m = body?.metrics?.[name];
            const verdict = m?.verdict as Verdict;
            return (
              <div key={name}
                className={cn("rounded-md p-3", _verdictBg(verdict))}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {METRIC_LABEL[name]}
                </div>
                <div className={cn("mt-0.5 text-xl font-semibold", _verdictColor(verdict))}>
                  {_formatMetric(name, m?.p75)}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-3 text-[11px] text-muted-foreground">
        Tip: The <strong>Failing × Traffic</strong> tab joins this data
        with GSC impressions to surface the high-ROI pages to fix first.
      </div>
    </Card>
  );
}


// ─── Trends tab ────────────────────────────────────────────────────


function TrendsTab({ propertyId: _propertyId }: { propertyId: number }) {
  return (
    <Card>
      <div className="flex items-center gap-2">
        <TrendingDown className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold">Trends</h3>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Origin-level CWV time-series. Each weekly pull writes a snapshot
        into the <strong>cwv_snapshots</strong> substrate; trends will
        render here once 4+ weeks of history accumulate.
      </p>
      <p className="mt-2 text-xs text-muted-foreground">
        Until then, the <strong>cwv_regression</strong> cross-source
        extractor will surface significant week-over-week regressions
        as findings (visible on the Insights lens).
      </p>
    </Card>
  );
}


// ─── Lighthouse tab ────────────────────────────────────────────────


function LighthouseTab({ propertyId }: { propertyId: number }) {
  const psi = useCapabilitySample("crux", "psi", { propertyId });
  const body = (psi.data?.response as PSIResponse | undefined);
  const lab = body?.lab;
  const noLab = !lab || lab.no_data;

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center gap-2">
          <Gauge className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">PageSpeed Insights — Lab Lighthouse</h3>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Synthetic Lighthouse run on Google's clean machine. Different from
          field — Google ranks on field; lab tells us WHY the field is bad.
        </p>
        {noLab ? (
          <div className="mt-3 rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
            No PSI data yet. Click <strong>Refresh</strong> at the top.
          </div>
        ) : (
          <>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <ScoreCard label="Performance"     score={lab.scores?.performance} />
              <ScoreCard label="SEO"              score={lab.scores?.seo} />
              <ScoreCard label="Accessibility"   score={lab.scores?.accessibility} />
              <ScoreCard label="Best practices"  score={lab.scores?.["best-practices"]} />
            </div>
            {lab.runtime_error && (
              <div className="mt-3 rounded-md border border-red-500/40 bg-red-500/10 px-2 py-1 text-xs text-red-700 dark:text-red-400">
                Runtime error: {lab.runtime_error}
              </div>
            )}
          </>
        )}
      </Card>

      {!noLab && lab?.core_web_vitals && (
        <Card>
          <h3 className="text-sm font-semibold">Lab Core Web Vitals (synthetic)</h3>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {Object.entries(lab.core_web_vitals).map(([name, audit]) => (
              <div key={name} className="rounded-md border border-border bg-muted/30 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {name.replace(/-/g, " ")}
                </div>
                <div className="mt-0.5 text-base font-semibold">
                  {audit?.displayValue ?? "—"}
                </div>
                {audit?.score !== undefined && audit?.score !== null && (
                  <div className="text-[10px] text-muted-foreground">
                    score {Math.round(audit.score * 100)}/100
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}


function ScoreCard({ label, score }: { label: string; score?: number | null }) {
  const v = score ?? null;
  const color = v === null
    ? "text-muted-foreground"
    : v >= 90 ? "text-emerald-700 dark:text-emerald-400"
    : v >= 50 ? "text-amber-700 dark:text-amber-400"
    : "text-red-700 dark:text-red-400";
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn("mt-0.5 text-2xl font-semibold", color)}>
        {v === null ? "—" : `${Math.round(v)}/100`}
      </div>
    </div>
  );
}


// ─── Failing × Traffic tab ─────────────────────────────────────────


function FailingTrafficTab({ propertyId: _propertyId }: { propertyId: number }) {
  return (
    <Card>
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-700 dark:text-amber-400" />
        <h3 className="text-sm font-semibold">High-traffic pages with failing CWV</h3>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Joins <code className="font-mono">gsc_pages</code> × <code className="font-mono">cwv_snapshots</code> to
        surface pages that BOTH get traffic AND fail Core Web Vitals on
        mobile. Each is the highest-ROI CWV fix on the site.
      </p>
      <p className="mt-2 text-xs text-muted-foreground">
        This list is rendered on the <strong>Insights</strong> lens
        when the <code className="font-mono">failing_cwv_with_traffic</code> finding fires.
        Run the GSC pull then the CWV pull; the cross-source extractors
        will populate this on the next Insights refresh.
      </p>
    </Card>
  );
}


// ─── Mobile vs Desktop tab ────────────────────────────────────────


function CompareFormFactorTab({ propertyId }: { propertyId: number }) {
  const psiMobile = useCapabilitySample("crux", "psi", { propertyId });
  const psiDesktop = useCapabilitySample("crux", "psi", { propertyId });

  const mobileOrigin = (psiMobile.data?.response as PSIResponse | undefined)?.field_origin;
  const desktopOrigin = (psiDesktop.data?.response as PSIResponse | undefined)?.field_origin;

  const mMetrics = mobileOrigin?.no_data ? undefined : mobileOrigin?.metrics;
  const dMetrics = desktopOrigin?.no_data ? undefined : desktopOrigin?.metrics;

  return (
    <div className="space-y-4">
      <Card>
        <h3 className="text-sm font-semibold">Origin field-data side-by-side</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          When mobile fails metrics that desktop passes, the bottleneck
          is mobile-specific (JS bundle, image size, mobile-only libs).
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="border-b border-border text-left">
              <tr>
                <th className="px-2 py-1.5 font-medium text-muted-foreground">Metric</th>
                <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">📱 Mobile</th>
                <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">🖥 Desktop</th>
                <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Gap</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(["largest_contentful_paint", "interaction_to_next_paint",
                 "cumulative_layout_shift", "first_contentful_paint",
                 "experimental_time_to_first_byte"] as const).map((name) => {
                const m = mMetrics?.[name];
                const d = dMetrics?.[name];
                const mp = typeof m?.p75 === "string" ? Number(m.p75) : m?.p75;
                const dp = typeof d?.p75 === "string" ? Number(d.p75) : d?.p75;
                const gap = (mp != null && dp != null) ? mp - dp : null;
                const gapColor = gap == null
                  ? "text-muted-foreground"
                  : Math.abs(gap) < (METRIC_GOOD_THRESHOLD[name] * 0.2) ? "text-muted-foreground"
                  : "text-amber-700 dark:text-amber-400";
                return (
                  <tr key={name}>
                    <td className="px-2 py-1.5 font-medium">{METRIC_LABEL[name]}</td>
                    <td className={cn("px-2 py-1.5 text-right", _verdictColor(m?.verdict as Verdict))}>
                      {_formatMetric(name, m?.p75)}
                    </td>
                    <td className={cn("px-2 py-1.5 text-right", _verdictColor(d?.verdict as Verdict))}>
                      {_formatMetric(name, d?.p75)}
                    </td>
                    <td className={cn("px-2 py-1.5 text-right font-mono", gapColor)}>
                      {gap == null ? "—" : (gap > 0 ? "+" : "") + _formatMetric(name, gap)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}


// ─── Recommendations tab ──────────────────────────────────────────


function RecommendationsTab({ propertyId }: { propertyId: number }) {
  const psi = useCapabilitySample("crux", "psi", { propertyId });
  const body = (psi.data?.response as PSIResponse | undefined);
  const lab = body?.lab;

  if (!lab || lab.no_data) {
    return (
      <Card>
        <div className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
          No Lighthouse data yet. Click <strong>Refresh</strong> at the
          top to pull PSI for the homepage.
        </div>
      </Card>
    );
  }

  const opps = lab.top_opportunities ?? [];
  const seo = lab.seo_failures ?? [];
  const a11y = lab.a11y_failures ?? [];

  const auditedUrl = lab.final_url || body?.url || "(unknown URL)";
  const strategy = body?.strategy ?? "(unknown)";

  return (
    <div className="space-y-4">
      <Card>
        <div className="text-[11px] text-muted-foreground">
          Recommendations below are from a Lighthouse run against{" "}
          <a href={auditedUrl} target="_blank" rel="noreferrer"
             className="font-mono hover:underline">{auditedUrl}</a>{" "}
          on strategy{" "}
          <span className="rounded bg-muted px-1 py-0.5 font-mono text-foreground">{strategy}</span>
          {lab.fetch_time && (
            <> at {new Date(lab.fetch_time).toLocaleString()}</>
          )}
          .
        </div>
      </Card>

      <RecommendationList
        title="Top performance opportunities"
        subtitle="Lighthouse audits with measurable savings — each item names the SPECIFIC resource to fix."
        icon={<Lightbulb className="h-4 w-4 text-primary" />}
        items={opps}
      />
      <RecommendationList
        title="SEO audit failures"
        subtitle="Lighthouse SEO checks the page failed."
        icon={<ListChecks className="h-4 w-4 text-primary" />}
        items={seo}
      />
      <RecommendationList
        title="Accessibility failures"
        subtitle="Lighthouse a11y checks (top 30)."
        icon={<ListChecks className="h-4 w-4 text-primary" />}
        items={a11y}
      />
    </div>
  );
}


function RecommendationList({ title, subtitle, icon, items }: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  items: LighthouseAudit[];
}) {
  if (items.length === 0) {
    return (
      <Card>
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-sm font-semibold">{title}</h3>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
        <div className="mt-2 rounded-md border border-dashed border-border px-3 py-2 text-center text-xs text-emerald-700 dark:text-emerald-400">
          ✓ Nothing to flag.
        </div>
      </Card>
    );
  }
  return (
    <Card>
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400">
          {items.length}
        </span>
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
      <ul className="mt-3 space-y-4">
        {items.map((item, i) => (
          <RecommendationItem key={item.id ?? i} item={item} />
        ))}
      </ul>
    </Card>
  );
}


function RecommendationItem({ item }: { item: LighthouseAudit }) {
  const savings = (() => {
    const ms = item.details?.overall_savings_ms;
    const bytes = item.details?.overall_savings_bytes;
    const parts: string[] = [];
    if (typeof ms === "number" && ms > 0) parts.push(`save ~${ms.toFixed(0)}ms`);
    if (typeof bytes === "number" && bytes > 0) parts.push(`save ~${(bytes / 1024).toFixed(1)} KiB`);
    return parts.join(" · ");
  })();

  return (
    <li className="border-l-2 border-amber-500/40 pl-3">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-xs font-semibold">{item.title ?? item.id}</h4>
        <div className="flex shrink-0 items-center gap-2">
          {item.displayValue && (
            <span className="font-mono text-[11px] text-muted-foreground">{item.displayValue}</span>
          )}
          {item.score !== undefined && item.score !== null && (
            <span className={cn(
              "rounded px-1.5 py-0.5 font-mono text-[10px]",
              item.score >= 0.9 ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" :
              item.score >= 0.5 ? "bg-amber-500/10 text-amber-700 dark:text-amber-400" :
                                  "bg-red-500/10 text-red-700 dark:text-red-400",
            )}>
              {Math.round(item.score * 100)}/100
            </span>
          )}
        </div>
      </div>
      {item.description && (
        <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
          {item.description.slice(0, 320)}{item.description.length > 320 ? "…" : ""}
        </p>
      )}
      {savings && (
        <div className="mt-1 inline-block rounded bg-emerald-500/10 px-1.5 py-0.5 font-mono text-[10px] text-emerald-700 dark:text-emerald-400">
          {savings}
        </div>
      )}
      {item.details?.items && item.details.items.length > 0 && (
        <DetailsTable details={item.details} />
      )}
      {item.warnings && item.warnings.length > 0 && (
        <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-800 dark:text-amber-300">
          <strong>Warnings:</strong>
          <ul className="ml-3 mt-0.5 list-disc">
            {item.warnings.slice(0, 5).map((w, j) => (
              <li key={j}>{String(w).slice(0, 240)}</li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}


function DetailsTable({ details }: { details: LighthouseAuditDetails }) {
  const items = details.items ?? [];
  if (items.length === 0) return null;

  // Build a column list — prefer headings if Lighthouse gave them; otherwise
  // infer from the first item's keys. Limit to the most useful columns.
  const headings = (details.headings ?? []).filter((h) => h.key);
  const useHeadings = headings.length > 0;

  // Useful inferred-key order if no headings
  const PREFERRED_KEYS = [
    "url", "source", "label", "node", "name", "responseTime",
    "totalBytes", "wastedBytes", "wastedMs", "wastedPercent",
    "duration", "transferSize", "startTime",
  ];

  const inferredKeys = useHeadings
    ? []
    : Array.from(new Set(items.flatMap((it) => Object.keys(it))))
        .filter((k) => PREFERRED_KEYS.includes(k))
        .sort((a, b) => PREFERRED_KEYS.indexOf(a) - PREFERRED_KEYS.indexOf(b))
        .slice(0, 5);

  const columns = useHeadings
    ? headings.slice(0, 5).map((h) => ({ key: h.key!, label: h.label ?? h.key!, valueType: h.valueType }))
    : inferredKeys.map((k) => ({ key: k, label: k, valueType: undefined as string | undefined }));

  if (columns.length === 0) return null;

  const renderCell = (value: unknown, valueType?: string): React.ReactNode => {
    if (value == null) return "—";

    // Lighthouse "node" type — rich object describing a DOM element.
    // Has snippet (HTML), selector (CSS path), nodeLabel (text content),
    // explanation (rule violation detail). selector + nodeLabel are
    // the most actionable; explanation is great for hover-tooltip.
    if (valueType === "node" && typeof value === "object" && !Array.isArray(value)) {
      const n = value as {
        snippet?: string; selector?: string; nodeLabel?: string;
        explanation?: string; path?: string;
      };
      const labelText = n.nodeLabel ? n.nodeLabel.slice(0, 60) : "";
      return (
        <div className="space-y-0.5"
             title={n.explanation ? n.explanation.slice(0, 600) : undefined}>
          {n.selector && (
            <div className="font-mono text-[10px] text-foreground">
              {n.selector.slice(0, 100)}
            </div>
          )}
          {n.snippet && (
            <code className="block text-[10px] text-muted-foreground">
              {n.snippet.slice(0, 80)}
            </code>
          )}
          {labelText && (
            <div className="text-[10px] italic text-muted-foreground">
              "{labelText}"
            </div>
          )}
        </div>
      );
    }

    // Lighthouse "source-location" — { url, line, column, urlProvider }
    if (valueType === "source-location" && typeof value === "object" && !Array.isArray(value)) {
      const sl = value as { url?: string; line?: number; column?: number };
      if (sl.url) {
        const short = sl.url.replace(/^https?:\/\//, "").replace(/^www\./, "").slice(0, 70);
        const loc = sl.line != null ? `:${sl.line}${sl.column != null ? ":" + sl.column : ""}` : "";
        return (
          <a href={sl.url} target="_blank" rel="noreferrer"
             className="font-mono text-[11px] hover:underline" title={sl.url}>
            {short}{loc}
          </a>
        );
      }
      return JSON.stringify(value).slice(0, 80);
    }

    // Lighthouse "code" — typically just a snippet string wrapped in {type, value}
    if (valueType === "code" && typeof value === "object" && !Array.isArray(value)) {
      const c = value as { value?: string };
      if (c.value) return <code className="text-[10px]">{String(c.value).slice(0, 100)}</code>;
    }

    // Lighthouse "link" — { type:'link', url, text }
    if (valueType === "link" && typeof value === "object" && !Array.isArray(value)) {
      const l = value as { url?: string; text?: string };
      if (l.url) {
        return (
          <a href={l.url} target="_blank" rel="noreferrer"
             className="font-mono text-[11px] hover:underline" title={l.url}>
            {l.text || l.url.replace(/^https?:\/\//, "").replace(/^www\./, "").slice(0, 80)}
          </a>
        );
      }
    }

    // Generic object fallback (when no valueType specified)
    if (typeof value === "object" && !Array.isArray(value)) {
      const obj = value as {
        url?: string; type?: string; snippet?: string;
        selector?: string; nodeLabel?: string; text?: string;
        value?: string;
      };
      // Treat as node-shape if it looks like one
      if (obj.selector || obj.nodeLabel) {
        return renderCell(value, "node");
      }
      if (obj.url) {
        return (
          <a href={obj.url} target="_blank" rel="noreferrer"
             className="font-mono text-[11px] hover:underline" title={obj.url}>
            {obj.url.replace(/^https?:\/\//, "").replace(/^www\./, "").slice(0, 80)}
          </a>
        );
      }
      if (obj.text) return <span className="text-[11px]">{String(obj.text).slice(0, 100)}</span>;
      if (obj.value) return <code className="text-[10px]">{String(obj.value).slice(0, 100)}</code>;
      if (obj.snippet) {
        return <code className="text-[10px]">{String(obj.snippet).slice(0, 80)}</code>;
      }
      return <span className="text-[10px] text-muted-foreground">{JSON.stringify(value).slice(0, 80)}</span>;
    }
    if (typeof value === "string") {
      // Format URLs nicely
      if (value.startsWith("http")) {
        return (
          <a href={value} target="_blank" rel="noreferrer"
             className="font-mono text-[11px] hover:underline" title={value}>
            {value.replace(/^https?:\/\//, "").replace(/^www\./, "").slice(0, 80)}
          </a>
        );
      }
      return value.length > 80 ? `${value.slice(0, 80)}…` : value;
    }
    if (typeof value === "number") {
      // Heuristic formatting by valueType (when Lighthouse provides it) or column name
      if (valueType === "bytes") return `${(value / 1024).toFixed(1)} KiB`;
      if (valueType === "ms" || valueType === "timespanMs") return `${value.toFixed(0)}ms`;
      if (valueType === "numeric") return value.toLocaleString();
      return value.toLocaleString();
    }
    return String(value);
  };

  return (
    <div className="mt-2 overflow-x-auto rounded-md border border-border">
      <table className="min-w-full text-[11px]">
        <thead className="border-b border-border bg-muted/40">
          <tr>
            {columns.map((c) => (
              <th key={c.key}
                className="px-2 py-1 text-left font-medium text-muted-foreground">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {items.slice(0, 10).map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c.key} className="px-2 py-1 align-top">
                  {renderCell(row[c.key], c.valueType)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {items.length > 10 && (
        <div className="border-t border-border bg-muted/20 px-2 py-1 text-[10px] text-muted-foreground">
          Showing 10 of {items.length} items
        </div>
      )}
    </div>
  );
}
